import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MDBRow, MDBCol, MDBBtn, MDBIcon, MDBAccordion, MDBAccordionItem, MDBSpinner, MDBInput } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { getUrl } from 'aws-amplify/storage';
import BilingualEditor from '../../../components-dev-admin/shared/BilingualEditor';
import TagInput from '../../../components-dev-admin/shared/TagInput';
import getEventDays from '../../../utils/getEventDays';

const PUBLIC_IMAGE_BASE = 'https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/';

const stripTypename = (value) => {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach(k => {
      if (k === '__typename') return;
      out[k] = stripTypename(value[k]);
    });
    return out;
  }
  return value;
};

const setBilingual = (list, language, text) => {
  const arr = Array.isArray(list) ? [...list] : [];
  const idx = arr.findIndex(item => item?.language === language);
  if (idx === -1) {
    arr.push({ language, text });
  } else {
    arr[idx] = { ...arr[idx], text };
  }
  return arr;
};

const getBilingual = (list, language) => {
  if (!Array.isArray(list)) return '';
  return list.find(item => item?.language === language)?.text || '';
};

const VideoPreview = ({ s3Path }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!s3Path) return;
    (async () => {
      try {
        const result = await getUrl({ key: s3Path.replace('public/', '') });
        if (!cancelled) setUrl(result.url.toString());
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Path]);

  if (error) return <div className="text-danger small">Could not load video</div>;
  if (!url) return <MDBSpinner size="sm" />;
  return (
    <video src={url} controls style={{ maxWidth: '100%', maxHeight: 240 }} />
  );
};

// Press media file: new uploads live in the Amplify bucket (s3Path + original file name),
// legacy entries only have a uuid file name in the public EventImages bucket.
const PressMediaItem = ({ file, onDownload, onRemove }) => {
  const [url, setUrl] = useState(file.s3Path ? null : `${PUBLIC_IMAGE_BASE}${file.fileName}`);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!file.s3Path) return;
    (async () => {
      try {
        const result = await getUrl({ key: file.s3Path.replace('public/', '') });
        if (!cancelled) setUrl(result.url.toString());
      } catch (e) {
        if (!cancelled) setPreviewFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [file.s3Path]);

  const previewStyle = { width: '100%', height: 120, objectFit: 'cover' };

  return (
    <div className="border rounded p-2">
      {previewFailed ? (
        <div className="d-flex align-items-center justify-content-center bg-light" style={previewStyle}>
          <MDBIcon far icon="file" size="2x" />
        </div>
      ) : !url ? (
        <div className="d-flex align-items-center justify-content-center" style={previewStyle}>
          <MDBSpinner size="sm" />
        </div>
      ) : (
        <img
          src={url}
          alt={file.fileName || ''}
          onError={() => setPreviewFailed(true)}
          style={previewStyle}
        />
      )}
      <div className="small text-truncate mt-2" title={file.fileName}>{file.fileName}</div>
      <div className="d-flex gap-2 mt-2">
        {file.s3Path && onDownload && (
          <MDBBtn color="secondary" size="sm" onClick={() => onDownload(file.s3Path)}>
            <MDBIcon fas icon="download" />
          </MDBBtn>
        )}
        <MDBBtn color="danger" size="sm" onClick={onRemove}>
          <MDBIcon fas icon="trash" />
        </MDBBtn>
      </div>
    </div>
  );
};

const WebsiteTab = ({ event, onInputChange, reinitializeEditors, onHighlightVideoUpload, onHighlightImageUpload, onPressMediaUpload, onFileDownload }) => {
  const { t } = useTranslation();

  const highlights = useMemo(() => stripTypename(event?.highlights) || {}, [event?.highlights]);

  const updateHighlights = useCallback((mutator) => {
    const next = mutator({ ...(highlights || {}) });
    onInputChange('highlights', next);
  }, [highlights, onInputChange]);

  const updateLandingPage = useCallback((field, language, html) => {
    const updated = setBilingual(event?.[field], language, html);
    onInputChange(field, updated);
  }, [event, onInputChange]);

  const heroBannerStats = useMemo(() => stripTypename(event?.heroBannerStats) || [], [event?.heroBannerStats]);

  const heroBannerStatsByIndex = useMemo(() => {
    const de = heroBannerStats.find(s => s?.language === 'de')?.text || [];
    const en = heroBannerStats.find(s => s?.language === 'en')?.text || [];
    const len = Math.max(de.length, en.length);
    const slots = [];
    for (let i = 0; i < len; i++) {
      slots.push({ de: de[i] || '', en: en[i] || '' });
    }
    return slots;
  }, [heroBannerStats]);

  const writeHeroBannerStats = (slots) => {
    onInputChange('heroBannerStats', [
      { language: 'de', text: slots.map(s => s.de || '') },
      { language: 'en', text: slots.map(s => s.en || '') },
    ]);
  };

  const handleHeroStatChange = (idx, language, value) => {
    const next = heroBannerStatsByIndex.map(s => ({ ...s }));
    if (!next[idx]) next[idx] = { de: '', en: '' };
    next[idx][language] = value;
    writeHeroBannerStats(next);
  };

  const handleHeroStatAdd = () => {
    writeHeroBannerStats([...heroBannerStatsByIndex, { de: '', en: '' }]);
  };

  const handleHeroStatRemove = (idx) => {
    writeHeroBannerStats(heroBannerStatsByIndex.filter((_, i) => i !== idx));
  };

  const updateInfoText = useCallback((field, language, html) => {
    const updated = setBilingual(event?.[field], language, html);
    onInputChange(field, updated);
  }, [event, onInputChange]);

  const contactPersons = useMemo(() => stripTypename(event?.contactPersons) || [], [event?.contactPersons]);

  const writeContactPersons = (next) => {
    onInputChange('contactPersons', next);
  };

  const handleContactChange = (idx, field, value) => {
    const next = contactPersons.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    writeContactPersons(next);
  };

  const handleContactAdd = () => {
    writeContactPersons([...contactPersons, { name: '', department: '', phone: '', email: '', image: null }]);
  };

  const handleContactRemove = (idx) => {
    writeContactPersons(contactPersons.filter((_, i) => i !== idx));
  };

  const handleContactImageUpload = async (idx, file) => {
    if (!file) return;
    const result = await onHighlightImageUpload(file);
    if (!result) return;
    const next = contactPersons.map((p, i) => i === idx ? { ...p, image: { fileName: result.fileName, alt: p?.name || '' } } : p);
    writeContactPersons(next);
  };

  const handleContactImageRemove = (idx) => {
    const next = contactPersons.map((p, i) => i === idx ? { ...p, image: null } : p);
    writeContactPersons(next);
  };

  const updateMainText = useCallback((language, html) => {
    updateHighlights(h => ({ ...h, mainText: setBilingual(h.mainText, language, html) }));
  }, [updateHighlights]);

  const handleMainVideoUpload = async (file) => {
    if (!file) return;
    const result = await onHighlightVideoUpload(file);
    if (!result) return;
    updateHighlights(h => ({ ...h, mainVideo: result }));
  };

  const handleMainVideoRemove = () => {
    updateHighlights(h => ({ ...h, mainVideo: null }));
  };

  const eventDays = useMemo(() => {
    if (!event?.startDate || !event?.endDate) return [];
    try {
      return getEventDays({ startDate: event.startDate, endDate: event.endDate });
    } catch {
      return [];
    }
  }, [event?.startDate, event?.endDate]);

  const dailysByDate = useMemo(() => {
    const map = {};
    (highlights.dailys || []).forEach(d => {
      if (d?.date) map[d.date] = d;
    });
    return map;
  }, [highlights.dailys]);

  const upsertDaily = (date, mutator) => {
    updateHighlights(h => {
      const existing = (h.dailys || []).find(d => d?.date === date) || { date, videos: [], text: [] };
      const next = mutator({ ...existing });
      const others = (h.dailys || []).filter(d => d?.date !== date);
      return { ...h, dailys: [...others, next].sort((a, b) => (a.date || '').localeCompare(b.date || '')) };
    });
  };

  const handleDailyVideoUpload = async (date, file) => {
    if (!file) return;
    const result = await onHighlightVideoUpload(file);
    if (!result) return;
    upsertDaily(date, d => ({ ...d, videos: [...(d.videos || []), result] }));
  };

  const handleDailyVideoRemove = (date, index) => {
    upsertDaily(date, d => ({ ...d, videos: (d.videos || []).filter((_, i) => i !== index) }));
  };

  const handleDailyTextChange = (date, language, html) => {
    upsertDaily(date, d => ({ ...d, text: setBilingual(d.text, language, html) }));
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    const result = await onHighlightImageUpload(file);
    if (!result) return;
    updateHighlights(h => ({ ...h, images: [...(h.images || []), { image: result, tags: [] }] }));
  };

  const handleGalleryImageRemove = (index) => {
    updateHighlights(h => ({ ...h, images: (h.images || []).filter((_, i) => i !== index) }));
  };

  const handleGalleryImageTagsChange = (index, tags) => {
    updateHighlights(h => ({
      ...h,
      images: (h.images || []).map((img, i) => i === index ? { ...img, tags } : img),
    }));
  };

  const handleGalleryVideoUpload = async (file) => {
    if (!file) return;
    const result = await onHighlightVideoUpload(file);
    if (!result) return;
    updateHighlights(h => ({ ...h, videos: [...(h.videos || []), { file: result, tags: [] }] }));
  };

  const handleGalleryVideoRemove = (index) => {
    updateHighlights(h => ({ ...h, videos: (h.videos || []).filter((_, i) => i !== index) }));
  };

  const handleGalleryVideoTagsChange = (index, tags) => {
    updateHighlights(h => ({
      ...h,
      videos: (h.videos || []).map((v, i) => i === index ? { ...v, tags } : v),
    }));
  };

  const handlePressTextChange = (language, html) => {
    updateHighlights(h => ({
      ...h,
      pressMedia: { ...(h.pressMedia || {}), text: setBilingual(h.pressMedia?.text, language, html) },
    }));
  };

  const handlePressMediaUpload = async (file) => {
    if (!file) return;
    const result = await onPressMediaUpload(file);
    if (!result) return;
    updateHighlights(h => ({
      ...h,
      pressMedia: { ...(h.pressMedia || {}), media: [...(h.pressMedia?.media || []), result] },
    }));
  };

  const handlePressMediaRemove = (index) => {
    updateHighlights(h => ({
      ...h,
      pressMedia: { ...(h.pressMedia || {}), media: (h.pressMedia?.media || []).filter((_, i) => i !== index) },
    }));
  };

  const handlePricingTextChange = (language, html) => {
    updateHighlights(h => ({
      ...h,
      exhibitorInfo: { ...(h.exhibitorInfo || {}), pricingText: setBilingual(h.exhibitorInfo?.pricingText, language, html) },
    }));
  };

  const renderLandingPageEditor = (title, field) => (
    <BilingualEditor
      key={`${field}-${reinitializeEditors}`}
      title={title}
      contentDe={getBilingual(event?.[field], 'de')}
      contentEn={getBilingual(event?.[field], 'en')}
      onChangeDe={(html) => updateLandingPage(field, 'de', html)}
      onChangeEn={(html) => updateLandingPage(field, 'en', html)}
    />
  );

  const renderLandingPageTitleInputs = (titleField) => (
    <MDBRow className="mb-3">
      <MDBCol md="6" className="mb-2 mb-md-0">
        <MDBInput
          label={`${t('Title')} (${t('German')})`}
          value={getBilingual(event?.[titleField], 'de')}
          onChange={(e) => updateLandingPage(titleField, 'de', e.target.value)}
        />
      </MDBCol>
      <MDBCol md="6">
        <MDBInput
          label={`${t('Title')} (${t('English')})`}
          value={getBilingual(event?.[titleField], 'en')}
          onChange={(e) => updateLandingPage(titleField, 'en', e.target.value)}
        />
      </MDBCol>
    </MDBRow>
  );

  const landingPageSections = [
    { id: 'intro', title: 'Introduction Text', field: 'landingPageIntroText' },
    { id: 'program', title: 'Program Text', field: 'landingPageProgramText' },
    { id: 'speakers', title: 'Program Speakers Text', field: 'landingPageProgramSpeakersText', titleField: 'landingPageProgramSpeakersTitle' },
    { id: 'booking', title: 'Booking Text', field: 'landingPageBookingText' },
    { id: 'pandc', title: 'Booking Payment & Cancelation Text', field: 'landingPageBookingPandCText' },
  ];

  return (
    <>
      <h5 className="mb-3">{t('Landing Page Texts')}</h5>
      <MDBAccordion alwaysOpen className="mb-4">
        {landingPageSections.map(section => (
          <MDBAccordionItem
            key={section.id}
            collapseId={`lp-${section.id}`}
            headerTitle={t(section.title)}
          >
            {section.titleField && renderLandingPageTitleInputs(section.titleField)}
            {renderLandingPageEditor(section.title, section.field)}
          </MDBAccordionItem>
        ))}
        <MDBAccordionItem collapseId="lp-heroBannerStats" headerTitle={t('Hero Banner Stats')}>
          <p className="text-muted small">
            {t('Free-form bilingual stats shown in the landing page hero (e.g. "XX exhibitors", "XX visitors").')}
          </p>
          {heroBannerStatsByIndex.length === 0 && (
            <p className="text-muted">{t('No stats yet. Click "Add stat" to start.')}</p>
          )}
          {heroBannerStatsByIndex.map((slot, idx) => (
            <div key={idx} className="border rounded p-3 mb-2">
              <MDBRow>
                <MDBCol md="5">
                  <MDBInput
                    label={`${t('German')} #${idx + 1}`}
                    value={slot.de}
                    onChange={(e) => handleHeroStatChange(idx, 'de', e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="5">
                  <MDBInput
                    label={`${t('English')} #${idx + 1}`}
                    value={slot.en}
                    onChange={(e) => handleHeroStatChange(idx, 'en', e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="2" className="d-flex align-items-center">
                  <MDBBtn color="danger" size="sm" onClick={() => handleHeroStatRemove(idx)}>
                    <MDBIcon fas icon="trash" />
                  </MDBBtn>
                </MDBCol>
              </MDBRow>
            </div>
          ))}
          <MDBBtn color="secondary" outline size="sm" onClick={handleHeroStatAdd}>
            <MDBIcon fas icon="plus" className="me-1" /> {t('Add stat')}
          </MDBBtn>
        </MDBAccordionItem>
      </MDBAccordion>

      <h5 className="mt-4 mb-3">{t('Highlights')}</h5>
      <MDBAccordion alwaysOpen>
        {/* Main */}
        <MDBAccordionItem collapseId={1} headerTitle={t('Main')}>
          <h6>{t('Main Video')}</h6>
          {highlights.mainVideo?.s3Path ? (
            <div className="mb-3">
              <VideoPreview s3Path={highlights.mainVideo.s3Path} />
              <div className="mt-2">
                <small className="text-muted me-2">{highlights.mainVideo.fileName}</small>
                <MDBBtn color="danger" size="sm" onClick={handleMainVideoRemove}>
                  <MDBIcon fas icon="trash" className="me-1" /> {t('Remove')}
                </MDBBtn>
              </div>
            </div>
          ) : (
            <div className="mb-3">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleMainVideoUpload(e.target.files?.[0])}
                className="form-control"
              />
            </div>
          )}

          <h6 className="mt-3">{t('Main Text')}</h6>
          <BilingualEditor
            key={`mainText-${reinitializeEditors}`}
            title="Main Text"
            contentDe={getBilingual(highlights.mainText, 'de')}
            contentEn={getBilingual(highlights.mainText, 'en')}
            onChangeDe={(html) => updateMainText('de', html)}
            onChangeEn={(html) => updateMainText('en', html)}
          />
        </MDBAccordionItem>

        {/* Daily */}
        <MDBAccordionItem collapseId={2} headerTitle={t('Daily')}>
          {eventDays.length === 0 ? (
            <p className="text-warning">{t('Please set the event start and end date in the General tab first.')}</p>
          ) : (
            <MDBAccordion alwaysOpen>
              {eventDays.map((date, dayIdx) => {
                const daily = dailysByDate[date] || { date, videos: [], text: [] };
                return (
                  <MDBAccordionItem
                    key={date}
                    collapseId={`day-${dayIdx + 1}`}
                    headerTitle={`${t('Day')} ${dayIdx + 1} — ${date}`}
                  >
                    <h6>{t('Videos')}</h6>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        handleDailyVideoUpload(date, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                      className="form-control mb-2"
                    />
                    {(daily.videos || []).map((video, vi) => (
                      <div key={`${video.s3Path}-${vi}`} className="mb-2 p-2 border rounded">
                        <VideoPreview s3Path={video.s3Path} />
                        <div className="mt-2">
                          <small className="text-muted me-2">{video.fileName}</small>
                          <MDBBtn color="danger" size="sm" onClick={() => handleDailyVideoRemove(date, vi)}>
                            <MDBIcon fas icon="trash" />
                          </MDBBtn>
                        </div>
                      </div>
                    ))}

                    <h6 className="mt-3">{t('Text')}</h6>
                    <BilingualEditor
                      key={`daily-${date}-${reinitializeEditors}`}
                      title={`Day ${dayIdx + 1} Text`}
                      contentDe={getBilingual(daily.text, 'de')}
                      contentEn={getBilingual(daily.text, 'en')}
                      onChangeDe={(html) => handleDailyTextChange(date, 'de', html)}
                      onChangeEn={(html) => handleDailyTextChange(date, 'en', html)}
                    />
                  </MDBAccordionItem>
                );
              })}
            </MDBAccordion>
          )}
        </MDBAccordionItem>

        {/* Image Gallery */}
        <MDBAccordionItem collapseId={3} headerTitle={t('Image Gallery')}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              handleGalleryImageUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
            className="form-control mb-3"
          />
          <MDBRow>
            {(highlights.images || []).map((entry, idx) => (
              <MDBCol md="4" key={`${entry.image?.fileName}-${idx}`} className="mb-3">
                <div className="border rounded p-2">
                  {entry.image?.fileName && (
                    <img
                      src={`${PUBLIC_IMAGE_BASE}${entry.image.fileName}`}
                      alt={entry.image.alt || ''}
                      style={{ width: '100%', height: 140, objectFit: 'cover' }}
                    />
                  )}
                  <div className="mt-2">
                    <TagInput
                      tags={entry.tags || []}
                      onChange={(tags) => handleGalleryImageTagsChange(idx, tags)}
                    />
                  </div>
                  <MDBBtn color="danger" size="sm" className="mt-2" onClick={() => handleGalleryImageRemove(idx)}>
                    <MDBIcon fas icon="trash" className="me-1" />{t('Remove')}
                  </MDBBtn>
                </div>
              </MDBCol>
            ))}
          </MDBRow>
        </MDBAccordionItem>

        {/* Video Gallery */}
        <MDBAccordionItem collapseId={4} headerTitle={t('Video Gallery')}>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => {
              handleGalleryVideoUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
            className="form-control mb-3"
          />
          <MDBRow>
            {(highlights.videos || []).map((entry, idx) => (
              <MDBCol md="6" key={`${entry.file?.s3Path}-${idx}`} className="mb-3">
                <div className="border rounded p-2">
                  <VideoPreview s3Path={entry.file?.s3Path} />
                  <div className="mt-2">
                    <small className="text-muted">{entry.file?.fileName}</small>
                  </div>
                  <div className="mt-2">
                    <TagInput
                      tags={entry.tags || []}
                      onChange={(tags) => handleGalleryVideoTagsChange(idx, tags)}
                    />
                  </div>
                  <MDBBtn color="danger" size="sm" className="mt-2" onClick={() => handleGalleryVideoRemove(idx)}>
                    <MDBIcon fas icon="trash" className="me-1" />{t('Remove')}
                  </MDBBtn>
                </div>
              </MDBCol>
            ))}
          </MDBRow>
        </MDBAccordionItem>

        {/* Press Media */}
        <MDBAccordionItem collapseId={5} headerTitle={t('Press Media')}>
          <BilingualEditor
            key={`pressText-${reinitializeEditors}`}
            title="Press Text"
            contentDe={getBilingual(highlights.pressMedia?.text, 'de')}
            contentEn={getBilingual(highlights.pressMedia?.text, 'en')}
            onChangeDe={(html) => handlePressTextChange('de', html)}
            onChangeEn={(html) => handlePressTextChange('en', html)}
          />

          <h6 className="mt-3">{t('Media')}</h6>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              handlePressMediaUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
            className="form-control mb-3"
          />
          <MDBRow>
            {(highlights.pressMedia?.media || []).map((file, idx) => (
              <MDBCol md="3" key={`${file.s3Path || file.fileName}-${idx}`} className="mb-3">
                <PressMediaItem
                  file={file}
                  onDownload={onFileDownload}
                  onRemove={() => handlePressMediaRemove(idx)}
                />
              </MDBCol>
            ))}
          </MDBRow>
        </MDBAccordionItem>

        {/* Exhibitor Info */}
        <MDBAccordionItem collapseId={6} headerTitle={t('Exhibitor Info')}>
          <BilingualEditor
            key={`pricingText-${reinitializeEditors}`}
            title="Pricing Text"
            contentDe={getBilingual(highlights.exhibitorInfo?.pricingText, 'de')}
            contentEn={getBilingual(highlights.exhibitorInfo?.pricingText, 'en')}
            onChangeDe={(html) => handlePricingTextChange('de', html)}
            onChangeEn={(html) => handlePricingTextChange('en', html)}
          />
        </MDBAccordionItem>
      </MDBAccordion>

      <h5 className="mt-4 mb-3">{t('Info Texts')}</h5>
      <MDBAccordion alwaysOpen className="mb-4">
        <MDBAccordionItem collapseId="info-exhibitors" headerTitle={t('Info Text for Exhibitors')}>
          <BilingualEditor
            key={`infoExhibitors-${reinitializeEditors}`}
            title="Info Text for Exhibitors"
            contentDe={getBilingual(event?.infoTextExhibitors, 'de')}
            contentEn={getBilingual(event?.infoTextExhibitors, 'en')}
            onChangeDe={(html) => updateInfoText('infoTextExhibitors', 'de', html)}
            onChangeEn={(html) => updateInfoText('infoTextExhibitors', 'en', html)}
          />
        </MDBAccordionItem>
        <MDBAccordionItem collapseId="info-visitors" headerTitle={t('Info Text for Visitors')}>
          <BilingualEditor
            key={`infoVisitors-${reinitializeEditors}`}
            title="Info Text for Visitors"
            contentDe={getBilingual(event?.infoTextVisitors, 'de')}
            contentEn={getBilingual(event?.infoTextVisitors, 'en')}
            onChangeDe={(html) => updateInfoText('infoTextVisitors', 'de', html)}
            onChangeEn={(html) => updateInfoText('infoTextVisitors', 'en', html)}
          />
        </MDBAccordionItem>
        <MDBAccordionItem collapseId="info-travel" headerTitle={t('Info Text for Travel & Directions')}>
          <BilingualEditor
            key={`infoTravel-${reinitializeEditors}`}
            title="Info Text for Travel & Directions"
            contentDe={getBilingual(event?.infoTextTravel, 'de')}
            contentEn={getBilingual(event?.infoTextTravel, 'en')}
            onChangeDe={(html) => updateInfoText('infoTextTravel', 'de', html)}
            onChangeEn={(html) => updateInfoText('infoTextTravel', 'en', html)}
          />
        </MDBAccordionItem>
      </MDBAccordion>

      <h5 className="mt-4 mb-3">{t('Contact Persons')}</h5>
      <MDBAccordion alwaysOpen className="mb-4">
        <MDBAccordionItem collapseId="contact-persons" headerTitle={t('Contact Persons for the Event')}>
          {contactPersons.length === 0 && (
            <p className="text-muted">{t('No contact persons yet. Click "Add contact person" to start.')}</p>
          )}
          {contactPersons.map((person, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <div className="d-flex align-items-start" style={{ gap: '1rem' }}>
                <div style={{ width: 120, flexShrink: 0 }} className="text-center">
                  {person.image?.fileName ? (
                    <>
                      <img
                        src={`${PUBLIC_IMAGE_BASE}${person.image.fileName}`}
                        alt={person.image.alt || person.name || ''}
                        style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '50%' }}
                      />
                      <MDBBtn
                        color="link"
                        size="sm"
                        className="mt-1 p-0 text-danger"
                        onClick={() => handleContactImageRemove(idx)}
                      >
                        <MDBIcon fas icon="trash" className="me-1" />{t('Remove image')}
                      </MDBBtn>
                    </>
                  ) : (
                    <>
                      <div
                        className="d-flex align-items-center justify-content-center bg-light text-muted mx-auto mb-2"
                        style={{ width: 120, height: 120, borderRadius: '50%' }}
                      >
                        <MDBIcon fas icon="user" size="2x" />
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleContactImageUpload(idx, e.target.files?.[0]);
                          e.target.value = '';
                        }}
                        className="form-control form-control-sm"
                      />
                    </>
                  )}
                </div>
                <div className="flex-grow-1">
                  <MDBRow>
                    <MDBCol md="6" className="mb-2">
                      <MDBInput
                        label={t('Name')}
                        value={person.name || ''}
                        onChange={(e) => handleContactChange(idx, 'name', e.target.value)}
                      />
                    </MDBCol>
                    <MDBCol md="6" className="mb-2">
                      <MDBInput
                        label={t('Department')}
                        value={person.department || ''}
                        onChange={(e) => handleContactChange(idx, 'department', e.target.value)}
                      />
                    </MDBCol>
                    <MDBCol md="6" className="mb-2">
                      <MDBInput
                        label={t('Phone')}
                        value={person.phone || ''}
                        onChange={(e) => handleContactChange(idx, 'phone', e.target.value)}
                      />
                    </MDBCol>
                    <MDBCol md="6" className="mb-2">
                      <MDBInput
                        label={t('Email')}
                        type="email"
                        value={person.email || ''}
                        onChange={(e) => handleContactChange(idx, 'email', e.target.value)}
                      />
                    </MDBCol>
                  </MDBRow>
                  <MDBBtn color="danger" size="sm" onClick={() => handleContactRemove(idx)}>
                    <MDBIcon fas icon="trash" className="me-1" /> {t('Remove')}
                  </MDBBtn>
                </div>
              </div>
            </div>
          ))}
          <MDBBtn color="secondary" outline size="sm" onClick={handleContactAdd}>
            <MDBIcon fas icon="plus" className="me-1" /> {t('Add contact person')}
          </MDBBtn>
        </MDBAccordionItem>
      </MDBAccordion>
    </>
  );
};

export default WebsiteTab;
